import React, { useState, useCallback } from 'react';
import { Space, Avatar, Tag, Typography, Tooltip } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faChevronDown, faChevronUp, faPlus, faLockOpen, faLock } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';
import Table from '../../commonComponents/Table';
import TablePagination from '../../commonComponents/Pagination';
import { MerchantsTableProps, Merchant } from './types';
import { ColumnsType } from 'antd/es/table';
import { ActionIconButton, AddButton } from './styles';
import { getMerchantUserRoleLabel } from './constants';

const MerchantsTable: React.FC<MerchantsTableProps> = ({
  merchants,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  onEdit,
  onDelete,
  onUnlock,
  loading = false,
  isMerchantUserView = false,
  showRoleColumn = false,
  onAddStaff,
}) => {
  const { isDarkMode } = useTheme();
  const isAdmin = useSelector((state: RootState) => state.auth.role) === 'admin';
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const handleRowClick = useCallback(
    (record: Merchant) => {
      if (!isMerchantUserView) return;
      setExpandedRowKeys((prev) =>
        prev.includes(record.id) ? prev.filter((k) => k !== record.id) : [...prev, record.id]
      );
    },
    [isMerchantUserView]
  );

  const columns: ColumnsType<Merchant> = [
    {
      title: 'NAME',
      key: 'user',
      fixed: 'left',
      width: 130,
      render: (_, record) => {
        const isExpanded = expandedRowKeys.includes(record.id);
        const iconColor = isDarkMode ? '#e2e8f0' : '#4a5568';
        return (
          <Space size={4}>
            {isMerchantUserView && (
              <span
                style={{
                  display: 'inline-flex',
                  width: 14,
                  justifyContent: 'center',
                  color: iconColor,
                  flexShrink: 0,
                }}
                aria-hidden
              >
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronUp : faChevronDown}
                  fontSize={11}
                />
              </span>
            )}
            <Avatar icon={<UserOutlined />} />
            <div>
              <div style={{ fontWeight: 500 }}>
                {record.name || record.username || record.email}
              </div>
            </div>
          </Space>
        );
      },
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      render: (email) => email || '-',
    },
    {
      title: 'PHONE',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone) => phone || '-',
    },
    ...(isMerchantUserView || showRoleColumn
      ? [
          {
            title: 'ROLE',
            key: 'role',
            width: 160,
            render: (_: unknown, record: Merchant) =>
              record.role ? (
                <Tag color="blue">{getMerchantUserRoleLabel(record.role)}</Tag>
              ) : (
                '-'
              ),
          } as const,
        ]
      : []),
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 220,
      render: (_, record) => (
        <span onClick={(e) => e.stopPropagation()} role="presentation">
          <Space size="small">
            {isAdmin && (
              record.otpLockout?.locked ? (
                <Tooltip
                  title={`${record.otpLockout.failedAttempts} failed OTP attempts. Unlocks automatically at ${new Date(
                    record.otpLockout.resetTime
                  ).toLocaleString()}.`}
                >
                  <Tag
                    color="error"
                    icon={<FontAwesomeIcon icon={faLock} fontSize={10} />}
                    style={{ marginLeft: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    Locked
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip title="No OTP lockout on this account">
                  <Tag
                    color="success"
                    icon={<FontAwesomeIcon icon={faLockOpen} fontSize={10} />}
                    style={{ marginLeft: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    Active
                  </Tag>
                </Tooltip>
              )
            )}
            {isAdmin && record.otpLockout?.locked && (
              <Tooltip title="Unlock account (OTP locked)">
                <ActionIconButton
                  onClick={() => onUnlock?.(record)}
                  iconColor="#52c41a"
                >
                  <FontAwesomeIcon icon={faLockOpen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
                </ActionIconButton>
              </Tooltip>
            )}
            <ActionIconButton
              onClick={() => onEdit(record)}
              iconColor="#2D6CCA"
            >
              <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
            <ActionIconButton
              onClick={() => onDelete(record)}
              iconColor="#f5222d"
            >
              <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
            </ActionIconButton>
          </Space>
        </span>
      ),
    },
  ];

  const handlePaginationChange = (page: number, size: number) => {
    if (size && size !== pageSize) {
      onPageSizeChange(size);
    }
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const staffColumns: ColumnsType<Merchant> = [
    {
      title: 'NAME',
      key: 'name',
      dataIndex: 'name',
      render: (_: unknown, record: Merchant) =>
        record.name || record.username || record.email,
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || '-',
    },
    {
      title: 'PHONE',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || '-',
    },
    {
      title: 'ROLE',
      key: 'role',
      render: (_: unknown, record: Merchant) =>
        record.role ? (
          <Tag color="blue">{getMerchantUserRoleLabel(record.role)}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          {isAdmin && (
            record.otpLockout?.locked ? (
              <Tooltip
                title={`${record.otpLockout.failedAttempts} failed OTP attempts. Unlocks automatically at ${new Date(
                  record.otpLockout.resetTime
                ).toLocaleString()}.`}
              >
                <Tag
                  color="error"
                  icon={<FontAwesomeIcon icon={faLock} fontSize={10} />}
                  style={{ marginLeft: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  Locked
                </Tag>
              </Tooltip>
            ) : (
              <Tooltip title="No OTP lockout on this account">
                <Tag
                  color="success"
                  icon={<FontAwesomeIcon icon={faLockOpen} fontSize={10} />}
                  style={{ marginLeft: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  Active
                </Tag>
              </Tooltip>
            )
          )}
          {isAdmin && record.otpLockout?.locked && (
            <Tooltip title="Unlock account (OTP locked)">
              <ActionIconButton
                onClick={() => onUnlock?.(record)}
                iconColor="#52c41a"
              >
                <FontAwesomeIcon icon={faLockOpen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
              </ActionIconButton>
            </Tooltip>
          )}
          <ActionIconButton
            onClick={() => onEdit(record)}
            iconColor="#2D6CCA"
          >
            <FontAwesomeIcon icon={faPen} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
          <ActionIconButton
            onClick={() => onDelete(record)}
            iconColor="#f5222d"
          >
            <FontAwesomeIcon icon={faTrash} fontSize={14} color={isDarkMode ? '#fff' : '#222222'} />
          </ActionIconButton>
        </Space>
      ),
    },
  ];

  const renderStaffSection = (staff: Merchant[], managerId: string) => {
    const bg = isDarkMode ? '#2d3748' : '#f7fafc';
    const border = isDarkMode ? '#4a5568' : '#e2e8f0';
    const textColor = isDarkMode ? '#e2e8f0' : '#2d3748';
    const secondaryTextColor = isDarkMode ? '#a0aec0' : '#718096';

    return (
      <div
        style={{
          padding: '16px 24px',
          background: bg,
          borderRadius: 8,
          border: `1px solid ${border}`,
          margin: '8px 0',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: staff.length > 0 ? 12 : 0,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: textColor,
            }}
          >
            Staff ({staff.length})
          </div>
          {staff.length > 0 && (
            <AddButton
              type="primary"
              size="small"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => onAddStaff?.(managerId)}
            >
              Add Staff
            </AddButton>
          )}
        </div>

        {staff.length > 0 ? (
          <Table<Merchant>
            columns={staffColumns}
            dataSource={staff}
            rowKey="id"
            pagination={false}
            disableScroll
            emptyText="No staff"
            emptyDescription="No staff"
            headerBackgroundColor={isDarkMode ? '#2d3748' : '#edf2f7'}
            thPadding="14px 12px"
            tdPadding="14px 12px"
            removeVerticalBorders
            border="0"
            theme={isDarkMode ? 'dark' : 'light'}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 24px',
              minHeight: '200px',
            }}
          >
            <Typography.Text
              style={{
                fontSize: 14,
                color: secondaryTextColor,
                marginBottom: 24,
              }}
            >
              No staff members yet. Click below to add your first staff member.
            </Typography.Text>
            <AddButton
              type="primary"
              size="large"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={() => onAddStaff?.(managerId)}
            >
              Add Staff
            </AddButton>
          </div>
        )}
      </div>
    );
  };

  const expandable = isMerchantUserView
    ? {
        expandedRowRender: (record: Merchant) => renderStaffSection(record.staff || [], record.id),
        rowExpandable: () => true,
        expandedRowKeys,
        onExpand: (expanded: boolean, record: Merchant) => {
          setExpandedRowKeys((prev) =>
            expanded ? [...prev, record.id] : prev.filter((k) => k !== record.id)
          );
        },
        expandIcon: () => null,
        columnWidth: 0,
        expandRowByClick: false,
      }
    : undefined;

  const rowProps = isMerchantUserView
    ? (record: Merchant) => {
        return {
          onClick: () => handleRowClick(record),
          style: { cursor: 'pointer' },
          role: 'button',
          'aria-expanded': expandedRowKeys.includes(record.id),
          'aria-label': expandedRowKeys.includes(record.id) ? 'Collapse staff' : 'Expand staff',
        };
      }
    : undefined;

  return (
    <>
      <Table<Merchant>
        columns={columns}
        dataSource={merchants}
        rowKey="id"
        loading={loading}
        pagination={false}
        scroll={merchants.length > 0 ? { x: true, y: 600 } : undefined}
        disableScroll={merchants.length === 0}
        emptyText="No merchants found"
        emptyDescription="No merchants found"
        headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
        removeVerticalBorders={true}
        thPadding="15px 10px"
        tdPadding="10px 10px"
        firstChildBorderRadius="8px"
        border="0"
        borderBottomLeftRight={true}
        theme={isDarkMode ? 'dark' : 'light'}
        expandable={expandable}
        onRow={rowProps}
      />
      {merchants.length > 0 && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total records"
          padding="16px 12px 0px 12px"
        />
      )}
    </>
  );
};

export default MerchantsTable;